/**
 * County Selection Step Component
 * Initial step in plan creation wizard - allows users to select their project county
 * This determines AI availability and jurisdiction-specific requirements
 */

import { useCounties } from '../../hooks/useCounties';

interface CountySelectionStepProps {
  onCountySelected: (countyId: string, countyName: string) => void;
  initialCountyId?: string;
}

interface County {
  id: string;
  name: string;
}

// San Luis Obispo County is our featured county with AI support
const FEATURED_COUNTY = {
  id: 'san-luis-obispo',
  name: 'San Luis Obispo',
  isAIEnabled: true
};

export function CountySelectionStep({ onCountySelected }: CountySelectionStepProps) {
  const { counties, loading, error } = useCounties();

  // Find the featured county from the API response
  const featuredCounty = counties.find(
    c => c.name === FEATURED_COUNTY.name ||
    c.id === FEATURED_COUNTY.id
  );

  // Get other counties (non-featured)
  const otherCounties = counties.filter(
    c => c.id !== featuredCounty?.id
  );

  const handleCountySelect = (county: County) => {
    onCountySelected(county.id, county.name);
  };

  // Loading state
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
          <p className="mt-4 text-sm text-neutral-600">Loading available counties...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex-1">
              <p className="text-sm font-medium text-red-900">Failed to Load Counties</p>
              <p className="text-sm text-red-700 mt-1">
                Unable to load available counties. Please refresh the page or try again later.
              </p>
              {/* Fallback to SLO County */}
              <button
                onClick={() => handleCountySelect(FEATURED_COUNTY)}
                className="mt-3 btn-secondary text-sm"
              >
                Continue with San Luis Obispo County
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-2xl font-bold text-neutral-900 mb-2">Select Your County</h3>
        <p className="text-neutral-600">
          Choose the county where your construction project is located. This determines available features and compliance requirements.
        </p>
      </div>

      {/* Featured County - San Luis Obispo */}
      {featuredCounty && (
        <div className="mb-8">
          <h4 className="text-sm font-semibold text-neutral-700 uppercase tracking-wider mb-3">
            Featured County with AI Support
          </h4>
          <div className="bg-gradient-to-br from-primary-50 to-primary-100 border-2 border-primary-300 rounded-xl p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-primary-600 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-neutral-900">
                    {featuredCounty.name} County
                  </h3>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 mt-1">
                    AI-Powered Plans Available
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-3 mb-5">
              <div className="flex items-start gap-2">
                <svg className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-sm text-neutral-700">
                  <strong>AI-powered plan generation</strong> - Create plans in seconds with job descriptions
                </span>
              </div>
              <div className="flex items-start gap-2">
                <svg className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-sm text-neutral-700">
                  <strong>Full CalGreen 5.408 compliance</strong> - Meets county requirements
                </span>
              </div>
              <div className="flex items-start gap-2">
                <svg className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-sm text-neutral-700">
                  <strong>Complete waste facility directory</strong> - All local disposal options included
                </span>
              </div>
            </div>

            <button
              onClick={() => handleCountySelect(featuredCounty)}
              className="w-full btn-primary flex items-center justify-center gap-2"
              aria-label={`Select ${featuredCounty.name} County`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
              Select {featuredCounty.name} County
            </button>
          </div>
        </div>
      )}

      {/* Other Counties */}
      {otherCounties.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-neutral-700 uppercase tracking-wider mb-3">
            Other Available Counties
          </h4>
          <p className="text-sm text-neutral-600 mb-4">
            Manual plan creation available for all California counties. AI features coming soon to more areas.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {otherCounties.map((county) => (
              <div
                key={county.id}
                className="border border-neutral-200 rounded-lg p-4 hover:border-primary-300 hover:shadow-md transition-all cursor-pointer"
                onClick={() => handleCountySelect(county)}
                role="button"
                tabIndex={0}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleCountySelect(county);
                  }
                }}
                aria-label={`Select ${county.name} County`}
              >
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-neutral-900">{county.name}</h4>
                  <svg className="w-5 h-5 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
                <p className="text-xs text-neutral-500">
                  Manual plan creation • AI coming soon
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No counties available (edge case) */}
      {counties.length === 0 && !loading && !error && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-900">No Counties Available</p>
              <p className="text-sm text-amber-700 mt-1">
                County data is currently unavailable. You can continue with San Luis Obispo County.
              </p>
              <button
                onClick={() => handleCountySelect(FEATURED_COUNTY)}
                className="mt-3 btn-secondary text-sm"
              >
                Continue with San Luis Obispo County
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}